import { Badge, Tag } from '../ui'
import type { LabDemoProps } from './types'

/**
 * Generic placeholder used until each paradigm gets its own implementation.
 * Renders the paradigm header + a "coming soon" body inside the standard
 * two-column user-action / ontology-response frame.
 */
export function LabPlaceholder({ paradigm, lab }: LabDemoProps) {
  const scenario = paradigm.scenarios[0]

  return (
    <div className="space-y-5">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="flex items-baseline gap-3 mb-1.5">
            <span className="mono text-[28px] leading-none text-[color:var(--color-orion-amber)] font-light">
              {paradigm.number}
            </span>
            <h2
              className="text-[color:var(--color-orion-text)] font-medium leading-tight"
              style={{ fontSize: 'var(--text-h2)' }}
            >
              {paradigm.title}
            </h2>
          </div>
          <p
            className="text-[color:var(--color-orion-haze)] max-w-2xl"
            style={{ fontSize: 'var(--text-body)', lineHeight: 1.55 }}
          >
            {paradigm.tagline}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge tone="warn">{paradigm.triggerBadge}</Badge>
          <span className="text-[color:var(--color-orion-haze)] mono text-[length:var(--text-micro)]">→</span>
          <Badge tone="pass">{paradigm.outputBadge}</Badge>
        </div>
      </header>

      <div className="flex flex-col gap-4">
        <PlaceholderColumn
          label={lab.userActionLabel}
          accent="amber"
          body={
            scenario
              ? `${scenario.industry}\n${scenario.example}`
              : 'Scenario lands with the demo.'
          }
        />
        <PlaceholderColumn
          label={lab.ontologyResponseLabel}
          accent="cyan"
          body={paradigm.interactionNotes.join('\n\n')}
        />
      </div>

      <div className="surface-night border-faint rounded-[var(--radius-lg)] p-6 flex items-start gap-3">
        <Tag kind="rule" className="shrink-0">
          {lab.comingSoonLabel}
        </Tag>
        <p
          className="text-[color:var(--color-orion-haze)]"
          style={{ fontSize: 'var(--text-body)', lineHeight: 1.55 }}
        >
          {lab.comingSoonBody}
        </p>
      </div>
    </div>
  )
}

function PlaceholderColumn({
  label,
  accent,
  body,
}: {
  label: string
  accent: 'amber' | 'cyan'
  body: string
}) {
  const accentColor =
    accent === 'amber' ? 'var(--color-orion-amber)' : 'var(--color-orion-cyan)'
  return (
    <div
      className="surface-night border-faint rounded-[var(--radius-lg)] p-5 sm:p-6 min-h-[260px] flex flex-col"
      style={{ borderLeft: `2px solid color-mix(in oklch, ${accentColor} 60%, transparent)` }}
    >
      <div
        className="mono text-[length:var(--text-micro)] uppercase tracking-widest mb-3"
        style={{ color: accentColor }}
      >
        {label}
      </div>
      <p
        className="text-[color:var(--color-orion-haze)] whitespace-pre-line"
        style={{ fontSize: 'var(--text-small)', lineHeight: 1.6 }}
      >
        {body}
      </p>
    </div>
  )
}
